/* eslint-disable react/jsx-indent */
import { useEffect, useState } from 'react';
import { QuestionIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Code,
  Flex,
  Heading,
  Icon,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Text,
  Tooltip,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { updateProviderDetails } from '../../../../redux/providerBuilder/providerDetails';
import { getProviderDetails } from '../../../../redux/providerBuilder/providerDetails/selectors';
import { getSelectedInstances } from '../../../../redux/providerBuilder/selectedInstances/selectors';
import {
  setSelectedNetworkIndex,
  updateSelectedNetworkRequest,
} from '../../../../redux/providerBuilder/selectedNetworkRequest';
import { getSelectedNetworkRequest } from '../../../../redux/providerBuilder/selectedNetworkRequest/selectors';
import {
  updateActiveStep,
  updateBackAction,
} from '../../../../redux/providerBuilder/stepperDetails';
import { getActiveStep } from '../../../../redux/providerBuilder/stepperDetails/selectors';
import { isJSONParsable } from '../../../../utils/is-json-parsable';
import {
  addQuotesAroundReqBody,
  getAllDynamicPaths,
  getValueByPath,
  processReqBodyInJson,
  setValueByPath,
} from '../../../../utils/provider-builder/instance-selection-utils';
import { extractDomain } from '../../../../utils/provider-builder/proof-card-utils';
import {
  getDefaultProofCardText,
  initializeRegexBody,
  seperateURLComponents,
  urlSpanToRegexUrl,
  URLSpanType,
} from '../../../../utils/provider-builder/request-config-utils';
import NormalJsonViewer from '../request-selection/normal-json-viewer-screen';
import { getSelectedNetworkIndex } from '../../../../redux/providerBuilder/selectedNetworkRequest/selectors';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';
import { URLSpan, BodySniff, NetworkRequest, RequestConfigProps } from './types';

export const RequestConfig = (props: RequestConfigProps) => {
  const toast = useToast();
  const dispatch = useAppDispatch();

  const activeStep = useAppSelector(getActiveStep);
  const selectedNetworkRequest = useAppSelector(getSelectedNetworkRequest);
  const selectedInstances = useAppSelector(getSelectedInstances);
  const providerDetails = useAppSelector(getProviderDetails);
  const selectedNetworkIndex = useAppSelector(getSelectedNetworkIndex);

  const [urlSpans, setUrlSpans] = useState<URLSpan[]>([]);
  const [regexBody, setRegexBody] = useState<string>('');
  const [selectedText, setSelectedText] = useState<Selection | null>(null);
  const [buttonLoading, setButtonLoading] = useState<boolean>(false);
  const [isRequestBodyJson, setIsRequestBodyJson] = useState<boolean>(false);
  const getAllRequest = useAppSelector(getSelectedNetworkRequest);

  useEffect(() => {
    if (selectedNetworkRequest) {
      if (
        providerDetails?.httpProviderId?.length &&
        providerDetails?.url ===
          selectedNetworkRequest[selectedNetworkIndex]?.templateUrl &&
        providerDetails?.method === selectedNetworkRequest[selectedNetworkIndex].method
      ) {
        const currentSelectedNetworkRequest =
          selectedNetworkRequest[selectedNetworkIndex];
        if (currentSelectedNetworkRequest?.urlSpans?.length) {
          const urlSpans = [...currentSelectedNetworkRequest?.urlSpans];
          setUrlSpans(urlSpans);
        } else {
          const newUrlSpans = seperateURLComponents(currentSelectedNetworkRequest?.url);
          setUrlSpans(newUrlSpans);
        }

        let regexBody;
        if (currentSelectedNetworkRequest?.bodySniff !== undefined) {
          regexBody = currentSelectedNetworkRequest?.bodySniff?.template;
        } else {
          regexBody = providerDetails?.bodySniff?.template;
        }

        const regexBodyWithQuotes = addQuotesAroundReqBody(regexBody);
        const cleanedRegexBody = regexBodyWithQuotes.replace(/\\n/g, '');
        setRegexBody(cleanedRegexBody);
        setIsRequestBodyJson(isJSONParsable(cleanedRegexBody));
        // if (currentSelectedNetworkRequest?.isRequestBodyJson) {
        //   setIsRequestBodyJson(true);
        // } else {
        //   setIsRequestBodyJson(isJSONParsable(cleanedRegexBody));
        // }
      } else {
        const currentSelectedNetworkRequest =
          selectedNetworkRequest[selectedNetworkIndex];
        setUrlSpans(
          currentSelectedNetworkRequest?.urlSpans ??
            seperateURLComponents(currentSelectedNetworkRequest?.url)
        );

        let newRegexBody = '';
        if (currentSelectedNetworkRequest?.bodySniff?.enabled) {
          newRegexBody = addQuotesAroundReqBody(
            currentSelectedNetworkRequest?.bodySniff?.template
          );
        } else {
          newRegexBody = initializeRegexBody(
            currentSelectedNetworkRequest?.request?.body
          );
        }

        const cleanedRegexBody = newRegexBody.replace(/\\n/g, '');
        setIsRequestBodyJson(isJSONParsable(cleanedRegexBody));

        setRegexBody(cleanedRegexBody);
      }
    }
  }, [selectedNetworkIndex]);

  // Reset the body to its original state
  const resetRegexBody = () => {
    const newRegexBody = initializeRegexBody(
      selectedNetworkRequest[selectedNetworkIndex]?.request?.body
    );
    const bodyEnabled = !!selectedNetworkRequest[selectedNetworkIndex]?.request?.body;
    dispatch(
      updateSelectedNetworkRequest({
        bodySniff: {
          enabled: bodyEnabled,
          regex: '',
          template: bodyEnabled ? newRegexBody : '',
        },
        index: selectedNetworkIndex,
      })
    );
    const cleanedRegexBody = newRegexBody.replace(/\\n/g, '');
    setRegexBody(cleanedRegexBody);
    setSelectedText(null);
  };

  // set selected text to the selectedText state
  const handleTextSelect = () => {
    const selection = window.getSelection();
    if (selection !== null) {
      setSelectedText(selection);
    }
  };

  // replace the selected text in the body and make it dynamic
  const handleReplaceRegexBody = () => {
    // Replace the selected text with the replacement text
    if (selectedText?.toString()?.length) {
      let startOffset = selectedText.anchorOffset;
      let endOffset = selectedText.focusOffset;

      if (startOffset > endOffset) {
        const endTemp = endOffset;
        endOffset = startOffset;
        startOffset = endTemp;
      }

      let prevRegexBody = regexBody;
      const prefix = prevRegexBody.slice(0, startOffset);
      const suffix = prevRegexBody.slice(endOffset);

      const specialString = '{{REQ_BODY_999}}';
      prevRegexBody = `${prefix}${specialString}${suffix}`;

      let paramsCounter = 1;
      const dynamicVariables = prevRegexBody.match(/{{REQ_BODY_\d+}}/g);

      if (dynamicVariables) {
        for (const dynamicVariable of dynamicVariables) {
          prevRegexBody = prevRegexBody.replace(
            dynamicVariable,
            `{{REQ_BODY_${paramsCounter}}}`
          );
          paramsCounter++;
        }
      }

      const cleanedPrevRegexBody = prevRegexBody.replace(/\\n/g, '');
      setRegexBody(cleanedPrevRegexBody);

      setSelectedText(null);
    } else {
      toast({
        title: 'No text selected',
        description: 'Select the text you want to make dynamic from the body',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    }
  };

  // Makes all body content as dynamic in regex body
  const makeFullBodyDynamic = () => {
    setRegexBody('{{REQ_BODY_GRD}}');
    dispatch(
      updateSelectedNetworkRequest({
        bodySniff: { enabled: true, regex: '', template: '{{REQ_BODY_GRD}}' },
        index: selectedNetworkIndex,
      })
    );
    setSelectedText(null);
  };

  function toggleSelectedUrlSpan(index: number): void {
    if (urlSpans[index].type !== 'STATIC') {
      let paramsCount = 1;
      const totalLength = urlSpans.length;

      const updatedUrlSpans = urlSpans.map((url: any) => ({ ...url }));

      updatedUrlSpans.forEach((url: any, i: number) => {
        if (index === i) {
          if (url.type?.includes('URL_PARAMS')) {
            updatedUrlSpans[i].type = URLSpanType.CONSTANT;
          } else if (url.type === URLSpanType.CONSTANT) {
            // Check if the selected index is the last in the array
            if (index === totalLength - 1) {
              updatedUrlSpans[i].type = '{{URL_PARAMS_GRD}}';
            } else {
              updatedUrlSpans[i].type = `{{URL_PARAMS_${paramsCount++}}}`;
            }
          }
        } else if (url.type?.includes('URL_PARAMS') && index !== i) {
          if (i === totalLength - 1) {
            updatedUrlSpans[i].type = '{{URL_PARAMS_GRD}}';
          } else {
            updatedUrlSpans[i].type = `{{URL_PARAMS_${paramsCount++}}}`;
          }
        }
      });
      const templateUrl = urlSpanToRegexUrl(updatedUrlSpans);
      dispatch(
        updateSelectedNetworkRequest({
          templateUrl,
          urlSpans: updatedUrlSpans,
          index: selectedNetworkIndex,
        })
      );
      setUrlSpans(updatedUrlSpans);
    }
  }

  const SwitchTab = () => {
    return (
      <Box pb={3}>
        <Flex direction="column" gap={2}>
          <Text fontSize="sm" fontWeight="medium" color="gray.600">
            {`Request ${selectedNetworkIndex + 1} of ${
              Object.values(getAllRequest)?.filter(
                (request) => typeof request === 'object' && request.currentPageUrl
              ).length
            }`}
          </Text>
          <Select
            value={selectedNetworkIndex.toString()}
            onValueChange={(value: any) => {
              dispatch(setSelectedNetworkIndex(parseInt(value)));
            }}
          >
            <SelectTrigger className="w-[500px] bg-white border-gray-200 hover:border-gray-300 focus:border-blue-400 focus:ring-1 focus:ring-blue-400 text-sm">
              <SelectValue placeholder="Select a request" />
            </SelectTrigger>
            <SelectContent className="">
              {Object.values(getAllRequest)
                ?.filter(
                  (request) => typeof request === 'object' && request.currentPageUrl
                )
                .map((request: any, index: number) => (
                  <SelectItem key={index} value={index.toString()}>
                    {request.url.length > 80
                      ? `${request.url.substring(0, 40)}...${request.url.slice(-40)}`
                      : request.url}
                    {request.selectedData?.length > 0 && ' ✓'}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </Flex>
      </Box>
    );
  };

  const handleNext = async () => {
    // setButtonLoading(true)
    const templateUrl = urlSpanToRegexUrl(urlSpans);
    const responseSelections = selectedInstances.map(
      (instance: any) => instance.responseSelection
    );

    const proofCardText = getDefaultProofCardText(selectedInstances);
    const domainName = extractDomain(selectedNetworkRequest[selectedNetworkIndex]?.url);
    const proofCardTitle = domainName;

    dispatch(
      updateProviderDetails({
        url: templateUrl,
        urlType: 'TEMPLATE',
        bodySniff: {
          enabled: false,
          regex: '',
          template: '',
        },
        proofCardText: proofCardText,
        proofCardTitle: proofCardTitle,
        responseSelections,
      })
    );
    setButtonLoading(false);
    props.setActiveStepInnerForm(1);
  };

  const handleJsonKeyClick = (clickedPath: string, checked: boolean = false): void => {
    const jsonObject = JSON.parse(regexBody);
    const allDynamicPaths = getAllDynamicPaths(
      '',
      jsonObject,
      '',
      {},
      false,
      clickedPath,
      checked
    );

    for (const dynamicPath in allDynamicPaths) {
      let value = allDynamicPaths?.[dynamicPath] ?? '{{REQ_BODY_1}}';

      if (value === 'ORIGINAL_VALUE') {
        const orginalJSON = JSON.parse(
          selectedNetworkRequest[selectedNetworkIndex]?.request?.body
        );
        value = getValueByPath(orginalJSON, dynamicPath);
      }

      setValueByPath(jsonObject, dynamicPath.substring(1), value);
    }

    const updatedJsonString = JSON.stringify(jsonObject);

    const bodyEnabled = !!selectedNetworkRequest[selectedNetworkIndex]?.request?.body;
    const finalTemplateBody = processReqBodyInJson(updatedJsonString);

    dispatch(
      updateSelectedNetworkRequest({
        bodySniff: {
          enabled: bodyEnabled,
          regex: '',
          template: bodyEnabled ? finalTemplateBody : '',
        },
        index: selectedNetworkIndex,
      })
    );
    const cleanedUpdatedJsonString = updatedJsonString.replace(/\\n/g, '');
    setRegexBody(cleanedUpdatedJsonString);
  };

  const renderUrlSpans = (urlSpans: URLSpan[]): JSX.Element[] => {
    const spans = [];
    let i = 0;
    let j = 0;

    while (i < urlSpans.length) {
      const span = urlSpans[i];

      if (span.key === '/' || span.key === '.') {
        i++;
        continue;
      }

      if (span.key === '?' || span.key === '&') {
        let query = span.key;

        if (span.type?.includes('URL_PARAMS')) {
          query = span.type;
        }

        i++;
        while (
          i < urlSpans.length &&
          urlSpans[i].key !== '?' &&
          urlSpans[i].key !== '&' &&
          urlSpans[i].key !== '/'
        ) {
          if (urlSpans[i].type?.includes('URL_PARAMS')) {
            query += urlSpans[i].type;
          } else {
            query += urlSpans[i].key;
          }

          i++;
        }

        i--;
        const queryIndex = i;

        spans.push(
          <Flex key={queryIndex} alignItems="center" paddingLeft={`5px`}>
            <Text
              overflow="hidden"
              whiteSpace="pre-wrap"
              className="urlSpanAll medium-font cursor-pointer  underline"
              onClick={() => toggleSelectedUrlSpan(queryIndex)}
              fontWeight={500}
            >
              {query}
            </Text>
          </Flex>
        );
      } else {
        const spanIndex = i;
        const prevSpan = i - 1 >= 0 ? urlSpans[i - 1] : null;

        spans.push(
          <Flex key={spanIndex} alignItems="center" paddingLeft={`5px`}>
            {span.type !== 'STATIC' && i !== 1 && prevSpan && prevSpan.key !== '.' && (
              <span>/ </span>
            )}
            <Text
              overflow="hidden"
              ml={
                span.type !== 'STATIC' && i !== 1 && prevSpan && prevSpan.key !== '.'
                  ? '10px'
                  : '0'
              }
              whiteSpace="pre-wrap"
              className={`urlSpanAll medium-font cursor-pointer ${i !== 0 ? 'p-2 underline rounded-md' : ''}`}
              onClick={() => toggleSelectedUrlSpan(spanIndex)}
              fontWeight={500}
            >
              {span.type?.includes('URL_PARAMS') ? span.type : span.key}
            </Text>
          </Flex>
        );
      }

      i++;
      j++;
    }

    return spans;
  };

  const handleGoBack = () => {
    dispatch(updateBackAction(true));
    dispatch(updateActiveStep(activeStep - 1));
  };

  const resetUrlSpans = () => {
    try {
      const newUrlSpans = seperateURLComponents(
        selectedNetworkRequest[selectedNetworkIndex].url
      );
      dispatch(
        updateSelectedNetworkRequest({
          urlSpans: newUrlSpans,
          index: selectedNetworkIndex,
        })
      );
      setUrlSpans(newUrlSpans);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <>
      <Box width="100%">
        <VStack
          spacing={5}
          align="stretch"
          p={7}
          pb={0}
          h="calc(100vh - 240px)"
          overflowY="auto"
          overflowX="hidden"
        >
          <Box width="100%">
            <Flex direction="column" gap="2">
              <Flex justifyContent="space-between" alignItems="center">
                <div className="">
                  <Heading className="regular-font">Standardize the request url</Heading>
                  <Text className="medium-font" lineHeight="24px" color="#98A2B3">
                    Select parts of the URL that contain user specific identifiers (e.g.
                    userId, username)
                  </Text>
                </div>
                <div className="">
                  <SwitchTab />
                </div>
              </Flex>
              <Flex my={2}>
                <Button
                  onClick={resetUrlSpans}
                  size="sm"
                  style={{
                    marginRight: '1rem',
                    boxSizing: 'border-box',
                  }}
                  bgColor="black"
                  color="white"
                  colorScheme="black"
                >
                  <Tooltip
                    label="Reset the url to its original state"
                    style={{
                      marginTop: '3rem',
                      marginBottom: '3rem',
                    }}
                    aria-label="A tooltip"
                  >
                    Reset
                  </Tooltip>
                </Button>
              </Flex>
              <Flex
                gap={3}
                mt={1}
                // backgroundColor="#F9FAFB"
                className="bg-secondary"
                borderRadius="5px"
                px={5}
                py={10}
                flexWrap="wrap"
                // minHeight="20vh"
                maxHeight="30vh"
                overflowY="auto"
                overflowX="auto"
              >
                {renderUrlSpans(urlSpans)}
              </Flex>
            </Flex>
          </Box>
          <Box width="100%">
            <VStack align="top" width="100%" gap={2}>
              <Heading className="regular-font">Standardize the request body</Heading>
              <Text className="medium-font" lineHeight="24px" color="#98A2B3">
                Click on the keys of request data that contain user specific identifiers
                (e.g. userId, username)
              </Text>
              <Flex alignItems="center" justifyContent="space-between" my={2}>
                <Button
                  onClick={resetRegexBody}
                  size="sm"
                  style={{
                    marginRight: '1rem',
                    boxSizing: 'border-box',
                  }}
                  bgColor="black"
                  color="white"
                  colorScheme="black"
                >
                  <Tooltip
                    label="Reset the body to its original state"
                    style={{
                      marginTop: '3rem',
                      marginBottom: '3rem',
                    }}
                    aria-label="A tooltip"
                  >
                    Reset
                  </Tooltip>
                </Button>
                <Box>
                  {!isRequestBodyJson && (
                    <>
                      <Button
                        onClick={handleReplaceRegexBody}
                        isDisabled={!!!selectedText}
                        size="sm"
                        style={{
                          marginRight: '1rem',
                          marginTop: '1rem',
                          boxSizing: 'border-box',
                        }}
                        colorScheme="blackAlpha"
                        bgColor="black"
                      >
                        <Tooltip
                          label="Select the text you want to make dynamic and click this button"
                          aria-label="A tooltip"
                        >
                          Make Dynamic
                        </Tooltip>
                      </Button>
                      <Button
                        onClick={makeFullBodyDynamic}
                        size="sm"
                        isDisabled={!!!regexBody}
                        style={{
                          marginRight: '1rem',
                          marginTop: '1rem',
                          boxSizing: 'border-box',
                        }}
                        colorScheme="blackAlpha"
                        bgColor="black"
                      >
                        <Tooltip
                          label="Make all the body content dynamic"
                          aria-label="A tooltip"
                        >
                          Make All Dynamic
                        </Tooltip>
                      </Button>
                      <Popover closeOnBlur={false}>
                        <PopoverTrigger>
                          <Icon as={QuestionIcon} w={6} h={6} mt="13px" color="black" />
                        </PopoverTrigger>
                        <PopoverContent color="black">
                          <PopoverArrow />
                          <PopoverHeader>What to do here?</PopoverHeader>
                          <PopoverCloseButton />
                          <PopoverBody>
                            <Box>
                              <iframe
                                src="https://www.loom.com/embed/93646bd34665411d9145991f171e6685?sid=62619623-30f4-43e8-ae63-e05854fa9f5f"
                                allowFullScreen={true}
                              />
                              <Text className="medium-font" mt="20px">
                                Highlight the parts of the body that are not important in
                                identifying this request, and mark them as dynamic. Eg.
                                Personal Data, IDs, etc
                              </Text>
                            </Box>
                          </PopoverBody>
                        </PopoverContent>
                      </Popover>
                    </>
                  )}
                </Box>
              </Flex>

              <Box
                className="bg-secondary"
                borderRadius="5px"
                p={2}
                minHeight="20vh"
                maxHeight="40vh"
                overflowY="auto"
                overflowX="auto"
              >
                {regexBody !== '' && (
                  <Text ml="-10px" onMouseUp={handleTextSelect} width="100%">
                    <div style={{ overflow: 'auto' }}>
                      {isRequestBodyJson ? (
                        <NormalJsonViewer
                          data={JSON.parse(regexBody)}
                          onItemClick={handleJsonKeyClick}
                          searchQuery=""
                        />
                      ) : (
                        <Code
                          style={{
                            whiteSpace: 'pre-wrap',
                            width: '100%',
                            backgroundColor: 'transparent',
                            color: '#000',
                            fontWeight: 400,
                          }}
                          className="medium-font"
                          letterSpacing={1}
                        >
                          {regexBody}
                        </Code>
                      )}
                    </div>
                  </Text>
                )}
                {regexBody === '' && <Text>No Body</Text>}
              </Box>
            </VStack>
          </Box>
        </VStack>
        <Box w="full" mt={10} className="bg-secondary" borderRadius={'10'} px={30} py={4}>
          <Flex w="full" justifyContent="space-between">
            <Button
              backgroundColor="transparent"
              border="1px solid gray"
              borderRadius={10}
              fontWeight="normal"
              _hover={{
                fontWeight: 'bold',
              }}
              minWidth={'100px'}
              isDisabled={buttonLoading}
              onClick={handleGoBack}
            >
              Back
            </Button>
            <Flex alignItems="center" gap={3}>
              <Text color="gray.500" className="medium-font">
                4/7
              </Text>
              <Button
                backgroundColor="black"
                _hover={{ backgroundColor: 'gray.800' }}
                color="white"
                border="1px solid black"
                borderRadius={10}
                fontWeight="normal"
                colorScheme="none"
                isLoading={buttonLoading}
                minWidth={'100px'}
                onClick={() => {
                  handleNext();
                }}
              >
                Next
              </Button>
            </Flex>
          </Flex>
        </Box>
      </Box>
    </>
  );
};
